import { useEffect, useState } from 'react';

/**
 * Dev-sync J1 — runtime loader + hand-written type surface for the VENDORED
 * Plotly build.
 *
 * ★ Survey §C3 option (a), the recommended one: load
 * `/static/vendor/plotly/plotly-basic.min.js` (1,071,091 bytes, present in
 * the repo under static/vendor/ — it survived the Backbone deletion) at
 * runtime via script injection. Zero build change, zero bundle growth, and
 * BYTE-IDENTICAL rendering to what legacy ran (`universal_app.js:154`
 * registered the same file as RequireJS path 'plotly'), which is what makes
 * template+CSS+payload verification tractable with no runnable legacy
 * reference. Do not switch to `npm i plotly.js-basic-dist` — version drift
 * from the vendored build moves rendering right where verification is
 * weakest (survey risk 2).
 *
 * DELIBERATE improvement over legacy load timing, no behavior change: legacy
 * loaded Plotly on every page-with-a-header via header.js:13's eager
 * `fn.require("view.psychrometric_chart")`; here the script loads on the
 * popup's FIRST mount (button click, or the minimize-restore shell), so
 * non-chart page views never pay the 1 MB.
 *
 * The type surface below covers exactly what the port calls — Plotly.react /
 * restyle (J1, declutter), relayout + Fx.unhover (J2 annotations), toImage
 * (J3a download) — plus the `_fullLayout` internals declutterLabels reads
 * (psychrometric_chart.js:736-742: xaxis/yaxis `.c2p()` and `._length`).
 */

export interface PsyScatterTrace {
  /** nulls are Plotly line-segment separators (addTunnelTraces pushes
   * `ent, leav, null` triplets per hop). */
  x: (number | null)[];
  y: (number | null)[];
  mode: 'lines' | 'markers+text';
  line?: { color: string; width: number; dash?: string };
  marker?: {
    color: string | string[];
    size: number | number[];
    symbol?: string | string[];
    line?: { width: number; color: string };
  };
  text?: string[];
  textposition?: string | string[];
  /** `color` may be per-point — the process-label trace colours each name
   * by its own component's season/staleness. */
  textfont?: { size: number; color: string | string[] };
  hovertext?: string[];
  hovertemplate?: string;
  hoverlabel?: {
    align?: string;
    bgcolor?: string;
    bordercolor?: string;
    font?: { size?: number; color?: string; family?: string };
  };
  hoverinfo?: string;
  opacity?: number;
  name: string;
}

/** The slice of Plotly's internal axis object declutterLabels reads. */
export interface PlotlyAxisInternal {
  range: [number, number];
  _length?: number;
  c2p?: (v: number) => number;
}

/** The chart's DOM node after Plotly.react has run on it. */
export interface PlotlyChartNode extends HTMLDivElement {
  _fullLayout?: {
    xaxis?: PlotlyAxisInternal;
    yaxis?: PlotlyAxisInternal;
  };
  /** Plotly's event emitter (plotly_hover/plotly_unhover — J2). */
  on?: (event: string, cb: (evt: unknown) => void) => void;
}

export interface PlotlyStatic {
  react(
    node: HTMLElement,
    data: unknown[],
    layout: Record<string, unknown>,
    config?: Record<string, unknown>
  ): Promise<unknown>;
  restyle(
    node: HTMLElement,
    update: Record<string, unknown>,
    traces?: number[]
  ): Promise<unknown>;
  relayout(node: HTMLElement, update: Record<string, unknown>): Promise<unknown>;
  toImage(node: HTMLElement, opts: Record<string, unknown>): Promise<string>;
  Fx: { unhover(node: HTMLElement): void };
}

declare global {
  interface Window {
    Plotly?: PlotlyStatic;
  }
}

const PLOTLY_SRC = '/static/vendor/plotly/plotly-basic.min.js';

let plotlyPromise: Promise<PlotlyStatic> | null = null;

/** Inject the vendored script once; every caller shares one promise. A load
 * failure clears the singleton so a later mount can retry. */
export function loadPlotly(): Promise<PlotlyStatic> {
  if (window.Plotly) return Promise.resolve(window.Plotly);
  if (!plotlyPromise) {
    plotlyPromise = new Promise<PlotlyStatic>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PLOTLY_SRC;
      script.async = true;
      script.onload = () => {
        if (window.Plotly) resolve(window.Plotly);
        else reject(new Error('Plotly script loaded but window.Plotly is missing'));
      };
      script.onerror = () => {
        plotlyPromise = null;
        script.remove();
        reject(new Error(`Failed to load ${PLOTLY_SRC}`));
      };
      document.head.appendChild(script);
    });
  }
  return plotlyPromise;
}

export interface PlotlyLoadState {
  /** null until the vendored build is ready. */
  plotly: PlotlyStatic | null;
  /** true once the injection failed — the plot area will never draw, so the
   * popup must stop waiting on it (a spinner that never ends reads as a hang). */
  failed: boolean;
}

/** The load gate: null until the vendored build is ready. */
export function usePlotly(): PlotlyLoadState {
  const [plotly, setPlotly] = useState<PlotlyStatic | null>(() => window.Plotly ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (plotly) return undefined;
    let cancelled = false;
    loadPlotly()
      .then((p) => {
        if (!cancelled) setPlotly(p);
      })
      .catch((err) => {
        // The dialog still opens (sidebar renders from the same payload);
        // only the plot area stays blank. Legacy's failure mode was worse —
        // fn.require('plotly') rejection killed the whole view.
        if (!cancelled) setFailed(true);
        // eslint-disable-next-line no-console
        console.error('[psychrometrics] Plotly failed to load:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [plotly]);

  return { plotly, failed };
}
